import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'

import { ModuleRegistry } from 'ag-grid-community'
import { LicenseManager } from 'ag-grid-enterprise'
import { AllEnterpriseModule } from 'ag-grid-enterprise'
import { themeAlpine, colorSchemeDarkBlue } from 'ag-grid-community'
import {} from '../component/filter'
import { Footer } from './component/table-footer'
import { usePageContext } from '../../context/page.context'
import { FIT_AUTO, FIT_COLUMNS, FIT_MANUAL, FIT_PAGE, MENU_COL_ID, fitMenuColumn, measureColumns, mergeMeasured, sizedColumnDefs, syncTruncatedTitle } from './table-sizing'
import './table-ag.scss'
import { GridLoadingOverlay, GridNoRowsOverlay, GridOverlayContext, OVERLAY_EMPTY, resolveOverlayCase } from './component/grid-overlay'
import { isOwnClick } from './row-click'

// A zero state rebuilt by AG Grid within this long of first appearing still
// animates in (it is the same appearance); later rebuilds, e.g. on a poll, do not.
const ENTRANCE_WINDOW_MS = 1000

// The row height for a grid whose cells stack two lines -- a name over a
// .sw-cell-sub subline, beside a compact (28px) logo tile -- with comfortable
// padding. Pair it with `stacked` on those columns (table-columns.js).
export const ROW_HEIGHT_TWO_LINE = 56

// A click on a control inside a row is the control's, not the row's (row-click.js).
export { isOwnClick }

// Below this width (px, the grid's own box) a grid that asks for it
// (`pinFirstColumn`) keeps its first data column pinned on the left while the
// rest scroll sideways, so a row on a phone still says what it is.
export const NARROW_GRID_PX = 576

// The widest a column pinned for narrowness gets: 45% of the grid's box, so
// the scrolling columns keep room beside it.
export const PINNED_SHARE = 0.45
export const pinnedCap = box => Math.floor(box * PINNED_SHARE)

// The column definitions with the first data column pinned on the left
// (narrow) or explicitly unpinned (wide). A pinned column doesn't flex: it
// is sized to its content (autoSizeColumns in TableAG), never wider than
// `cap` and never forced wider by its own minWidth; unpinned, it flexes (its
// own flex, else 1) with its own minWidth back, and is then sized like every
// other column (table-sizing.js).
export const withPinnedFirst = (columns, narrow, cap) => {
	if (!columns) return columns
	const i = columns.findIndex((c) => c && c.colId !== 'menu' && c.field !== 'menu' && !c.hide && !c.pinned)
	if (i < 0) return columns
	const col = columns[i]
	const next = narrow
		? { ...col, pinned: 'left', flex: null, minWidth: Math.min(col.minWidth ?? 100, cap), maxWidth: cap }
		: { ...col, pinned: null, flex: col.flex || 1 }
	return columns.map((c, j) => (j === i ? next : c))
}

// The grid's column definitions for its pin state ({ narrow, cap, ever }).
// A grid that doesn't ask for the pin, or has never been narrow, gets its
// definitions untouched (the same array): a wide opted-in grid is exactly
// the grid it was.
export const pinnedColumnDefs = (columns, pin, pinFirstColumn) =>
	pinFirstColumn && pin && pin.ever ? withPinnedFirst(columns, pin.narrow, pin.cap) : columns

// The column definitions fitted to the page (every column but the menu and a
// pinned one flexes): the grid's first paint, before its columns are sized to
// their content. Kept here for the grids' callers; it lives in table-sizing.js.
export { withFitFlex } from './table-sizing'

// The colId AG Grid gives a definition (its colId, else its field).
const colIdOf = (col) => (col ? col.colId || col.field : null)

// Whether the box is narrow. A box that measures 0 (not laid out yet, or
// jsdom) is not: nothing is pinned until there is a real width.
export const isNarrow = width => width > 0 && width < NARROW_GRID_PX

// The box's width, rounded down to 10 px so a pinned column's cap doesn't
// follow every pixel of a resize.
const boxOf = el => Math.floor((el ? el.clientWidth : 0) / 10) * 10

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY)
ModuleRegistry.registerModules([AllEnterpriseModule])

const getExportName = (name) => {
	const today = new Date()
	const year = today.getFullYear()
	const month = (today.getMonth() + 1).toString().padStart(2, '0')
	const day = today.getDate().toString().padStart(2, '0')
	return `${name}-${year}-${month}-${day}`
}

// A cell starting with = + - @ (or a tab / carriage return) runs as a formula
// when the CSV is opened in a spreadsheet, and grids carry values that come from
// outside (emails, display names, identity-provider claims). Such a string is
// exported with a leading ' so it opens as text (OWASP CSV injection). Numbers
// are left alone, so a negative figure still exports as a number.
const FORMULA_START = /^[=+\-@\t\r]/

export const csvSafeCell = (params) => {
	// A callback replaces AG Grid's own valueFormatter step, so apply it here.
	const value = params.formatValue ? params.formatValue(params.value) : params.value
	if (value === null || value === undefined) return ''
	return typeof value === 'string' && FORMULA_START.test(value) ? `'${value}` : value
}

export const TableAG = forwardRef(
	(
		{
			funcRef,
			columns,
			data,
			loading,
			rowModelType,
			infiniteInitialRowCount,
			cacheBlockSize,
			cacheOverflowSize,
			maxBlocksInCache,

			sortFirst,
			sortCol,
			sortDesc,

			onReady,
			onRefresh,
			onRowClicked,

			onScrollVertical,
			onSortChanged,
			onDataUpdated,
			onDestoryed,
			getRowId,
			// Optional AG Grid getRowClass: a class on a row (a highlight).
			getRowClass,
			// Optional row height (px). Only grids whose cells stack two lines or
			// a logo tile set it (ROW_HEIGHT_TWO_LINE); every other grid keeps
			// the theme's default height.
			rowHeight,

			// Zero states (see component/grid-overlay.js). `emptyState` is the
			// page's "nothing yet" copy and action; `noMatchState` overrides the
			// generic "no matches" copy. `searchActive` marks a search the grid
			// cannot see itself (a server-side search); quick-filter and column
			// filters are detected from the grid. `onClearSearch` is the owning
			// wrapper's own clear-search handler.
			emptyState,
			noMatchState,
			searchActive,
			onClearSearch,

			// Pin the first data column on the left when the grid is narrow
			// (NARROW_GRID_PX), and unpin it when it widens again.
			pinFirstColumn,

			// Select mode (grid-selection.js): while it is selecting, a click on a
			// row ticks or unticks it instead of opening it, and so does Space on
			// a focused cell of the row.
			selection
		},
		ref
	) => {
		const pageContext = usePageContext()

		const defaultColDef = useMemo(() => {
			return {
				minWidth: 100,
				sortable: true,
				enablePivot: true,
				enableRowGroup: true
			}
		}, [])

		const gridOptions = useMemo(() => {
			return {
				// Keep the user's column order when columnDefs are re-applied.
				//
				// AG Grid takes column order FROM the definitions every time they
				// change, so any re-render that hands it a fresh columnDefs array
				// silently discards a column the user dragged. Live-refreshing
				// tables make that constant rather than occasional: useLiveRefresh
				// calls setLastUpdatedAt on every tick, which re-renders the parent,
				// which rebuilds the array — so a column dragged to the left slid
				// back on its own every few seconds.
				maintainColumnOrder: true,
				// Every column's cells are drawn, also those scrolled out of view
				// sideways: a column is sized to its drawn cells (table-sizing.js),
				// and a column AG Grid had not drawn would be sized to its header.
				// The admin's grids have a dozen columns at most.
				suppressColumnVirtualisation: true
			}
		}, [])

		const sideBarConfig = useMemo(() => {
			return {
				toolPanels: [
					{
						id: 'columns',
						labelDefault: 'Columns',
						labelKey: 'columns',
						iconKey: 'columns',
						toolPanel: 'agColumnsToolPanel',
						minWidth: 225,
						maxWidth: 225,
						width: 225
					}
				],
				defaultToolPanel: 'columns',
				position: 'right'
			}
		}, [])

		const [sideBar, updateSideBar] = useState(null)

		useImperativeHandle(funcRef, () => ({
			exportCsv: (name) => {
				const exportName = name || 'export'
				const fileName = getExportName(exportName)
				ref.current.api.exportDataAsCsv({ fileName: fileName, processCellCallback: csvSafeCell })
			},
			exportExcel: () => ref.current.api.exportDataAsExcel(),
			toggleSideBar: () => {
				if (sideBar) updateSideBar(null)
				else updateSideBar(sideBarConfig)
			}
		}))

		const [pagination, updatePagination] = useState(false)

		const handleRefresh = () => onRefresh && onRefresh(ref.current)

		const [paginationValues, updatePaginationValues] = useState({})

		const handlePaginationChanged = (agGrid) => {
			let rowCount = agGrid.api.getDisplayedRowCount()
			const currentPage = agGrid.api.paginationGetCurrentPage()
			let pageSize = agGrid.api.paginationGetPageSize()
			let totalPages = agGrid.api.paginationGetTotalPages()

			updatePaginationValues({ rowCount, currentPage, pageSize, totalPages })
		}

		const theme = useMemo(() => {
			const base = pageContext.theme.isDark ? themeAlpine.withPart(colorSchemeDarkBlue).withParams({ backgroundColor: 'var(--edxr-surface)' }) : themeAlpine
			// The row height is also a theme param so AG Grid derives the cells'
			// line-height from it: a one-line cell beside a stacked one stays
			// vertically centred in the taller row instead of riding at the top.
			return rowHeight ? base.withParams({ rowHeight }) : base
		}, [pageContext.theme.isDark, rowHeight])

		// A click on a control in the row (a button, the ⋮ toggle, a switch, a
		// checkbox, a link, anything marked data-no-row-click) is that control's,
		// not the row's: AG Grid still raises rowClicked for it, and a React
		// stopPropagation can't stop AG Grid's own listener (row-click.js).
		const selectionRef = useRef(selection)
		selectionRef.current = selection
		const handleRowClicked = (row) => {
			if (isOwnClick(row && row.event)) return
			const s = selectionRef.current
			if (s && s.selecting) {
				if (row && row.data) s.toggle(s.idOf(row.data))
				return
			}
			if (!onRowClicked) return
			onRowClicked(row.data)
		}

		// Space on a focused cell of a row in select mode ticks it (a control in
		// the cell, the box itself included, keeps its own keys).
		const handleCellKeyDown = (params) => {
			const s = selectionRef.current
			const e = params && params.event
			if (!s || !s.selecting || !e || e.key !== ' ' || !params.data || isOwnClick(e)) return
			e.preventDefault()
			s.toggle(s.idOf(params.data))
		}

		// Which "no rows" case the overlay shows. AG Grid only raises its own
		// no-rows overlay for an EMPTY client-side data set -- never when a search
		// or filter hides every row, and never for the infinite row model -- so the
		// overlay is driven here from the displayed row count instead.
		const [overlayCase, updateOverlayCase] = useState(OVERLAY_EMPTY)

		const containerRef = useRef()

		// Whether a no-rows overlay is up, as far as this component knows. It is
		// owned here rather than read back from the grid's DOM: api.showNoRowsOverlay()
		// builds a NEW overlay component on every call, so calling it on each model
		// update (every poll) would remount the zero state. Only public AG Grid API
		// is used, and the flag follows AG Grid's documented overlay behaviour:
		//   - for an EMPTY client-side data set AG Grid raises (and keeps) the
		//     no-rows overlay itself whenever it is not loading, so we never add one;
		//   - otherwise (rows hidden by a search/filter, or the infinite model) the
		//     overlay is ours, and AG Grid drops it when `loading` changes, when new
		//     row data arrives and when columns are reloaded -- the flag is cleared
		//     at those points and the overlay re-asserted.
		const overlayUpRef = useRef(false)

		const agOwnsOverlay = () => rowModelType !== 'infinite' && !(data && data.length)

		// When the current zero state first appeared. The entrance animation only
		// plays shortly after that, so an overlay AG Grid rebuilds on a later poll
		// (it does, for new row data that a filter still hides entirely) is never
		// re-animated. Cleared once rows are displayed again.
		const appearedRef = useRef(null)

		const syncOverlay = useCallback(
			(api) => {
				if (!api || loading || api.isDestroyed?.()) return

				if (api.getDisplayedRowCount() === 0) {
					const nextCase = resolveOverlayCase({ searchActive: !!searchActive, filterActive: api.isAnyFilterPresent() })
					if (appearedRef.current?.overlayCase !== nextCase) appearedRef.current = { overlayCase: nextCase, at: Date.now() }
					updateOverlayCase(nextCase)

					if (agOwnsOverlay()) {
						overlayUpRef.current = true
					} else if (!overlayUpRef.current) {
						api.showNoRowsOverlay()
						overlayUpRef.current = true
					}
				} else {
					api.hideOverlay()
					overlayUpRef.current = false
					appearedRef.current = null
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[loading, searchActive, rowModelType, data]
		)

		// After a `loading` change AG Grid re-evaluates its overlay (and drops a
		// no-rows overlay it did not raise itself); re-assert ours. This effect
		// runs after AgGridReact (a child) has applied the new prop.
		const lastLoadingRef = useRef(loading)
		useEffect(() => {
			if (lastLoadingRef.current !== loading) {
				lastLoadingRef.current = loading
				overlayUpRef.current = false
			}
			syncOverlay(ref.current?.api)
		}, [syncOverlay, ref, loading])

		// A model update is also a page of an infinite grid arriving, a sort or
		// a filter: the drawn rows changed, so the columns are measured again,
		// and a pinned first column is sized to them (an infinite grid raises
		// no rowDataUpdated, so this is where its rows arrive).
		const handleModelUpdated = (params) => {
			syncOverlay(params.api)
			scheduleMeasure()
			autoSizePinned(params.api)
		}

		const handleNewColumnsLoaded = (params) => {
			overlayUpRef.current = false
			syncOverlay(params.api)
		}

		// A column shown, hidden or unpinned (the columns panel, a narrow grid
		// widening) is measured as it is drawn, as is a grid that was hidden
		// (a tab) and now has a size. The definitions measuring hands back
		// raise this too; nothing grows, so it settles there.
		const handleDisplayedColumnsChanged = () => scheduleMeasure()

		const shouldAnimate = useCallback(() => !!appearedRef.current && Date.now() - appearedRef.current.at < ENTRANCE_WINDOW_MS, [])

		const handleClearSearchAndFilters = useCallback(() => {
			ref.current?.api?.setFilterModel(null)
			onClearSearch && onClearSearch()
		}, [ref, onClearSearch])

		const overlayContext = useMemo(
			() => ({ overlayCase, emptyState, noMatchState, onClear: handleClearSearchAndFilters, shouldAnimate }),
			[overlayCase, emptyState, noMatchState, handleClearSearchAndFilters, shouldAnimate]
		)

		// AG Grid applies getRowClass only when it draws a row, so a new
		// getRowClass (a highlight that moved to another row) redraws the rows.
		const firstRowClassRef = useRef(true)
		useEffect(() => {
			if (firstRowClassRef.current) {
				firstRowClassRef.current = false
				return
			}
			const api = ref.current?.api
			if (api && !api.isDestroyed?.()) api.redrawRows()
		}, [getRowClass, ref])

		// The grid's box, followed with a ResizeObserver (pinFirstColumn
		// only): whether it is narrow, the pinned column's cap, and whether it
		// was ever narrow (a grid that never was keeps its definitions
		// untouched). The pin lives in the column definitions, so AG Grid
		// applies it on the first render and on change.
		const [pin, setPin] = useState({ narrow: false, cap: 0, ever: false })
		useLayoutEffect(() => {
			if (!pinFirstColumn || !containerRef.current) return undefined
			const el = containerRef.current
			const measure = () => {
				const box = boxOf(el)
				const narrow = isNarrow(box)
				setPin((p) => (p.narrow === narrow && (!narrow || p.cap === pinnedCap(box)) ? p : { narrow, cap: narrow ? pinnedCap(box) : p.cap, ever: p.ever || narrow }))
			}
			measure()
			if (typeof window.ResizeObserver !== 'function') return undefined
			const ro = new window.ResizeObserver(measure)
			ro.observe(el)
			return () => ro.disconnect()
		}, [pinFirstColumn])
		const { narrow } = pin

		// Column widths (table-sizing.js): each column is sized to its content
		// and header, measured from the drawn rows; the spare width is shared
		// out when the columns fit, else the grid scrolls sideways in its box.
		// `measured` only grows within a mount. `fit` is the sizing mode: auto,
		// or the person's own choice (footer "fit to columns" / "fit to page",
		// a dragged column edge); n re-applies a footer choice clicked again.
		const [measured, setMeasured] = useState(null)
		const [fit, setFit] = useState({ mode: FIT_AUTO, n: 0 })
		const columnDefs = useMemo(
			() => sizedColumnDefs(pinnedColumnDefs(columns, pin, pinFirstColumn), measured, fit.mode),
			// fit (not only fit.mode): a footer choice clicked again hands AG Grid
			// fresh definitions, so it re-applies them over a dragged column.
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[columns, pin, pinFirstColumn, measured, fit]
		)

		// Measuring reads the drawn rows, so it waits two frames for the cell
		// renderers to paint (as autoSizeMenu does) and coalesces the events
		// that ask for it into one pass. While the first rows load it sizes the
		// columns to their headers (the loading overlay covers the body). It
		// skips a loaded grid showing no rows whose zero state is TableAG's own
		// (an infinite grid, or a search hiding every row): new column
		// definitions make AG Grid drop that overlay, and it would be rebuilt
		// from scratch. An empty client-side grid's zero state is AG Grid's and
		// survives, so its headers are measured.
		const loadingRef = useRef(loading)
		loadingRef.current = loading
		const agOwnsOverlayRef = useRef(agOwnsOverlay)
		agOwnsOverlayRef.current = agOwnsOverlay
		const fitModeRef = useRef(fit.mode)
		fitModeRef.current = fit.mode
		const measureFrameRef = useRef(null)
		const measureTimerRef = useRef(null)
		const settleTimerRef = useRef(null)
		// The menu column is as wide as its drawn cells' icons, and never
		// narrows within a mount (fitMenuColumn): every measuring pass fits it,
		// so an infinite grid's rows (which raise no rowDataUpdated), rows
		// scrolled into view and a menu that changed (a role that loaded late)
		// widen it, in every sizing mode.
		const menuFloorRef = useRef(0)
		const fitMenu = useCallback((api) => {
			if (!api || api.isDestroyed?.()) return
			menuFloorRef.current = fitMenuColumn(api, containerRef.current, menuFloorRef.current)
		}, [])
		const measureNow = useCallback(() => {
			const api = ref.current?.api
			const root = containerRef.current
			if (!api || api.isDestroyed?.() || !root) return
			fitMenu(api)
			if (fitModeRef.current !== FIT_AUTO && fitModeRef.current !== FIT_MANUAL) return
			if (!loadingRef.current && !api.getDisplayedRowCount() && !agOwnsOverlayRef.current()) return
			const ids = (api.getAllDisplayedColumns() || []).filter((c) => !c.getPinned() && c.getColId() !== 'menu').map((c) => c.getColId())
			const next = measureColumns(root, ids)
			if (next) setMeasured((prev) => mergeMeasured(prev, next))
		}, [ref, fitMenu])
		// A pass two frames on, and one more once things settle (a cell renderer
		// or a web font that paints late, a slow first load).
		const measureSoon = useCallback(() => {
			if (measureFrameRef.current) return
			measureFrameRef.current = requestAnimationFrame(() => {
				measureFrameRef.current = requestAnimationFrame(() => {
					measureFrameRef.current = null
					try {
						measureNow()
					} catch (e) {}
				})
			})
		}, [measureNow])
		const scheduleMeasure = useCallback(() => {
			measureSoon()
			clearTimeout(settleTimerRef.current)
			settleTimerRef.current = setTimeout(measureSoon, 400)
		}, [measureSoon])
		useEffect(
			() => () => {
				if (measureFrameRef.current) cancelAnimationFrame(measureFrameRef.current)
				clearTimeout(measureTimerRef.current)
				clearTimeout(settleTimerRef.current)
			},
			[]
		)

		// The first rows are in (the loading overlay is down): size the columns.
		useEffect(() => {
			scheduleMeasure()
		}, [loading, scheduleMeasure])

		// A web font that arrives after the first measurement draws wider text.
		useEffect(() => {
			let live = true
			const fonts = typeof document !== 'undefined' && document.fonts
			if (fonts && fonts.ready && typeof fonts.ready.then === 'function') fonts.ready.then(() => live && scheduleMeasure())
			return () => {
				live = false
			}
		}, [scheduleMeasure])

		// "fit to columns": every column exactly its content, once the
		// definitions without flex are in.
		useEffect(() => {
			if (fit.mode !== FIT_COLUMNS) return undefined
			const api = ref.current?.api
			if (!api || !api.autoSizeColumns) return undefined
			const id = requestAnimationFrame(() => {
				try {
					if (api.isDestroyed?.()) return
					const ids = (api.getAllDisplayedColumns() || []).map((c) => c.getColId()).filter((colId) => colId !== 'menu')
					api.autoSizeColumns(ids, false)
				} catch (e) {}
			})
			return () => cancelAnimationFrame(id)
		}, [fit, ref])

		const handleFitToColumns = useCallback(() => setFit((f) => ({ mode: FIT_COLUMNS, n: f.n + 1 })), [])
		const handleFitToPage = useCallback(() => setFit((f) => ({ mode: FIT_PAGE, n: f.n + 1 })), [])

		// A column edge the person dragged (or double-clicked) keeps the width
		// they gave it: the definitions stop carrying flex.
		const handleColumnResized = (params) => {
			if (params && params.source === 'uiColumnResized' && params.finished) {
				setFit((f) => (f.mode === FIT_MANUAL ? f : { mode: FIT_MANUAL, n: f.n + 1 }))
			}
		}

		// A cell cut off at its column's width (long free text) shows its full
		// text as a tooltip.
		const handleCellMouseOver = (params) => {
			const target = params && params.event && params.event.target
			if (target && typeof target.closest === 'function') syncTruncatedTitle(target.closest('.ag-cell'))
		}

		// A pinned column takes its content's width (within its cap) once the
		// grid has drawn the pin, and again when new rows arrive.
		const pinnedColId = pinFirstColumn && narrow ? colIdOf(columnDefs && columnDefs.find((c) => c && c.pinned === 'left')) : null
		const autoSizePinned = useCallback(
			(api) => {
				if (!pinnedColId || !api || !api.autoSizeColumns) return
				requestAnimationFrame(() =>
					requestAnimationFrame(() => {
						try {
							if (api.isDestroyed?.()) return
							api.autoSizeColumns([pinnedColId], false)
						} catch (e) {}
					})
				)
			},
			[pinnedColId]
		)
		useEffect(() => {
			autoSizePinned(ref.current?.api)
		}, [autoSizePinned, ref])

		const autoSizeMenu = (api) => {
			if (!api || !api.autoSizeColumns) return
			// double rAF so the custom icon cells paint before AG Grid measures them
			requestAnimationFrame(() =>
				requestAnimationFrame(() => {
					try {
						// The grid may have unmounted in between.
						if (api.isDestroyed?.()) return
						api.autoSizeColumns([MENU_COL_ID], false)
						// AG Grid's autosize reads only the rows drawn now, and
						// may have narrowed the column: back to what the icons need.
						fitMenu(api)
					} catch (e) {}
				})
			)
		}

		// The column definitions already carry the fitted first paint (flex),
		// so the grid needs no fitting of its own here.
		const handleReady = async (agGrid) => {
			onReady && (await onReady(agGrid))
			autoSizeMenu(agGrid.api)
			autoSizePinned(agGrid.api)
		}

		const handleDataUpdated = (params) => {
			// New row data: AG Grid has just dropped any overlay of ours (see
			// overlayUpRef). Re-assert here -- modelUpdated is not guaranteed to
			// follow this event for transaction-style (getRowId) updates.
			overlayUpRef.current = false
			syncOverlay(params.api)
			onDataUpdated && onDataUpdated(params)
			autoSizeMenu(params.api)
			autoSizePinned(params.api)
			scheduleMeasure()
		}

		const handleBodyScroll = (params) => {
			if (params.direction === 'vertical') {
				if (onScrollVertical) {
					let index = ref.current?.api?.getFirstDisplayedRowIndex()
					onScrollVertical(index)
				}
				// Rows scrolled into view may hold a longer value (a block of an
				// infinite grid loaded further down): measure once scrolling rests.
				clearTimeout(measureTimerRef.current)
				measureTimerRef.current = setTimeout(scheduleMeasure, 200)
			}
		}

		return (
			<div ref={containerRef} className='d-flex flex-column h-100'>
				<div className='flex-fill'>
					<GridOverlayContext.Provider value={overlayContext}>
					<AgGridReact
						ref={ref}
						theme={theme}
						defaultColDef={defaultColDef}
						gridOptions={gridOptions}
						columnDefs={columnDefs}
						rowData={data}
						getRowId={getRowId}
						getRowClass={getRowClass}
						rowHeight={rowHeight}
						loading={loading}
						rowModelType={rowModelType}
						pagination={pagination}
						paginationAutoPageSize={pagination}
						rowBuffer={0}
						cacheBlockSize={cacheBlockSize}
						cacheOverflowSize={cacheOverflowSize}
						infiniteInitialRowCount={infiniteInitialRowCount}
						maxBlocksInCache={maxBlocksInCache}
						loadingOverlayComponent={GridLoadingOverlay}
						noRowsOverlayComponent={GridNoRowsOverlay}
						suppressPaginationPanel={true}
						onGridReady={handleReady}
						onPaginationChanged={handlePaginationChanged}
						pivotMode={false}
						sideBar={sideBar}
						rowGroupPanelShow={sideBar && 'always'}
						onRowClicked={handleRowClicked}
						onCellKeyDown={handleCellKeyDown}
						onBodyScroll={handleBodyScroll}
						onSortChanged={onSortChanged}
						onRowDataUpdated={handleDataUpdated}
						onGridPreDestroyed={onDestoryed}
						onModelUpdated={handleModelUpdated}
						onNewColumnsLoaded={handleNewColumnsLoaded}
						onFirstDataRendered={scheduleMeasure}
						onDisplayedColumnsChanged={handleDisplayedColumnsChanged}
						onGridSizeChanged={scheduleMeasure}
						onColumnResized={handleColumnResized}
						onCellMouseOver={handleCellMouseOver}
					/>
					</GridOverlayContext.Provider>
				</div>
				<div>
					<Footer
						gridRef={ref}
						pagination={pagination}
						paginationValues={paginationValues}
						onPaginationChange={updatePagination}
						onRefresh={handleRefresh}
						onFitToColumns={handleFitToColumns}
						onFitToPage={handleFitToPage}
					/>
				</div>
			</div>
		)
	}
)
